import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { queryClient } from '@/lib/queryClient'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Loader2 } from 'lucide-react'
import { apiService } from '@/services/apiService'
import { cn } from '@/lib/utils'
import type { components } from '../../../../../types/flex-api'

type Pool = components['schemas']['Pool']
type Phase = 'running' | 'done'

// Shape stored in React Query cache under ['tou', 'settings', systemID]
interface CachedTouInfo {
  touEnabled: boolean
  checkError?: string
}

interface RunError {
  systemID: string
  reason: string
}

interface Props {
  open: boolean
  onClose: () => void
  pool: Pool
}

// How many systems to check then enable per cycle
const POOL_BATCH_SIZE = 100
// Inner rate-limiting: pause every N API calls
const API_BATCH_SIZE = 10

function SummaryCard({ label, value, colorClass }: { label: string; value: number; colorClass: string }) {
  return (
    <div className="rounded-md border p-3 text-center">
      <p className={cn('text-2xl font-bold', colorClass)}>{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
    </div>
  )
}

export default function EnableTouDialog({ open, onClose, pool }: Props) {
  const [phase, setPhase] = useState<Phase>('running')
  const [progress, setProgress] = useState({ current: 0, total: 0 })
  const [batchLabel, setBatchLabel] = useState('')
  const [runErrors, setRunErrors] = useState<RunError[]>([])
  const [summary, setSummary] = useState({ enabled: 0, alreadyEnabled: 0, failed: 0 })

  const cancelledRef = useRef(false)
  const hasStartedRef = useRef(false)

  // Systems for this pool — served from cache after the first fetch (staleTime: 5 min)
  const { data: poolSystems } = useQuery({
    queryKey: ['systems', 'byPool', pool.poolID],
    queryFn: async () => {
      const resp = await apiService.systems.getByPool(pool.poolID)
      if (!resp.success) throw new Error(resp.error ?? 'Failed to fetch systems')
      return (resp.data as Array<{ systemID: string }>) ?? []
    },
    enabled: open,
  })

  // Reset whenever the dialog opens
  useEffect(() => {
    if (!open) return
    cancelledRef.current = false
    hasStartedRef.current = false
    setPhase('running')
    setProgress({ current: 0, total: 0 })
    setBatchLabel('')
    setRunErrors([])
    setSummary({ enabled: 0, alreadyEnabled: 0, failed: 0 })
  }, [open])

  // Start processing once systems are available
  useEffect(() => {
    if (!open || !poolSystems || hasStartedRef.current) return
    hasStartedRef.current = true
    runAll(poolSystems)
  }, [open, poolSystems]) // eslint-disable-line react-hooks/exhaustive-deps

  const runAll = async (systems: Array<{ systemID: string }>) => {
    const ids = systems.map((s) => s.systemID)
    const totalBatches = Math.ceil(ids.length / POOL_BATCH_SIZE)
    setProgress({ current: 0, total: ids.length })

    let enabledCount = 0
    let alreadyEnabledCount = 0
    let failedCount = 0
    const errorList: RunError[] = []

    for (let batchIdx = 0; batchIdx < totalBatches; batchIdx++) {
      if (cancelledRef.current) return

      const batchStart = batchIdx * POOL_BATCH_SIZE
      const batchIds = ids.slice(batchStart, batchStart + POOL_BATCH_SIZE)

      if (totalBatches > 1) {
        setBatchLabel(`Batch ${batchIdx + 1} of ${totalBatches}`)
      }

      // ── Phase 1: check ──────────────────────────────────────────────────
      const batchInfos: Array<{ systemID: string; touEnabled: boolean; failed: boolean }> = []

      for (let i = 0; i < batchIds.length; i++) {
        if (cancelledRef.current) return
        const systemID = batchIds[i]

        const cached = queryClient.getQueryData<CachedTouInfo>(['tou', 'settings', systemID])
        if (cached !== undefined) {
          batchInfos.push({ systemID, touEnabled: cached.touEnabled, failed: !!cached.checkError })
        } else {
          try {
            const resp = await apiService.tou.getSettings(systemID)
            if (resp.success) {
              const touEnabled = resp.data == null ? false : ((resp.data as any).enabled ?? false)
              queryClient.setQueryData<CachedTouInfo>(['tou', 'settings', systemID], { touEnabled })
              batchInfos.push({ systemID, touEnabled, failed: false })
            } else {
              queryClient.setQueryData<CachedTouInfo>(['tou', 'settings', systemID], {
                touEnabled: false,
                checkError: resp.error,
              })
              batchInfos.push({ systemID, touEnabled: false, failed: true })
            }
          } catch {
            batchInfos.push({ systemID, touEnabled: false, failed: true })
          }

          // Rate-limit API calls
          if ((i + 1) % API_BATCH_SIZE === 0 && i + 1 < batchIds.length) {
            await new Promise((r) => setTimeout(r, 200))
          }
        }

        setProgress({ current: batchStart + i + 1, total: ids.length })
      }

      // ── Phase 2: enable ─────────────────────────────────────────────────
      const toEnable = batchInfos.filter((s) => !s.touEnabled && !s.failed)

      for (let i = 0; i < toEnable.length; i++) {
        if (cancelledRef.current) return
        const { systemID } = toEnable[i]

        const resp = await apiService.tou.enable(systemID)
        if (resp.success) {
          queryClient.setQueryData<CachedTouInfo>(['tou', 'settings', systemID], { touEnabled: true })
          enabledCount++
        } else {
          queryClient.setQueryData<CachedTouInfo>(['tou', 'settings', systemID], {
            touEnabled: false,
            checkError: resp.error,
          })
          failedCount++
          errorList.push({ systemID, reason: resp.error || 'Enable failed' })
          setRunErrors([...errorList])
        }

        if ((i + 1) % API_BATCH_SIZE === 0 && i + 1 < toEnable.length) {
          await new Promise((r) => setTimeout(r, 200))
        }
      }

      // Accumulate counts from check failures
      for (const s of batchInfos) {
        if (s.failed) {
          failedCount++
          errorList.push({ systemID: s.systemID, reason: 'Check failed' })
          setRunErrors([...errorList])
        } else if (s.touEnabled) {
          alreadyEnabledCount++
        }
      }
    }

    if (cancelledRef.current) return
    setSummary({ enabled: enabledCount, alreadyEnabled: alreadyEnabledCount, failed: failedCount })
    setPhase('done')
  }

  const handleClose = () => {
    cancelledRef.current = true
    onClose()
  }

  const pct = progress.total > 0 ? (progress.current / progress.total) * 100 : 0

  return (
    <Dialog open={open} onOpenChange={(o) => !o && phase !== 'running' && handleClose()}>
      <DialogContent className="max-w-lg flex flex-col max-h-[85vh]">
        <DialogHeader>
          <DialogTitle>Enable Time of Use for Pool Systems</DialogTitle>
          <DialogDescription>
            {phase === 'running'
              ? 'Checking and enabling Time of Use — please wait.'
              : 'All operations complete.'}
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 min-h-0 py-1">
          {phase === 'running' && (
            <div className="flex flex-col items-center justify-center py-10 gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <div className="w-full space-y-1.5">
                <div className="flex justify-between text-xs text-muted-foreground">
                  {batchLabel ? (
                    <span>{batchLabel}</span>
                  ) : (
                    <span>{progress.total > 0 ? `System ${progress.current} of ${progress.total}` : 'Fetching systems…'}</span>
                  )}
                  {progress.total > 0 && <span>{progress.current} / {progress.total}</span>}
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
              {runErrors.length > 0 && (
                <p className="text-xs text-destructive self-start">
                  {runErrors.length} error{runErrors.length !== 1 ? 's' : ''} so far
                </p>
              )}
            </div>
          )}

          {phase === 'done' && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2">
                <SummaryCard label="Enabled" value={summary.enabled} colorClass="text-green-600" />
                <SummaryCard
                  label="Already enabled"
                  value={summary.alreadyEnabled}
                  colorClass="text-muted-foreground"
                />
                <SummaryCard
                  label="Failed"
                  value={summary.failed}
                  colorClass={summary.failed > 0 ? 'text-destructive' : 'text-muted-foreground'}
                />
              </div>

              {runErrors.length > 0 && (
                <div className="rounded-md border border-destructive/40 overflow-hidden">
                  <div className="px-3 py-2 bg-destructive/10 border-b border-destructive/40">
                    <span className="text-sm font-medium text-destructive">
                      {runErrors.length} system{runErrors.length !== 1 ? 's' : ''} failed
                    </span>
                  </div>
                  <table className="w-full text-xs">
                    <tbody>
                      {runErrors.map((e, idx) => (
                        <tr key={idx} className="border-b last:border-0">
                          <td className="p-2 pl-3 font-mono whitespace-nowrap">{e.systemID.slice(0, 8)}…</td>
                          <td className="p-2 text-muted-foreground">{e.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 pt-2">
          {phase === 'running' && (
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          )}
          {phase === 'done' && <Button onClick={handleClose}>Close</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
